// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetOrdersApiParameter } from "@insite/client-framework/Services/OrderService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadOrders from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrders";
import { getOrdersDataView } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import OrderSummaryCard, { OrderSummaryCardStyles } from "@insite/content-library/Components/OrderSummaryCard";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderPresentationProps } from "@insite/mobius/DataTable/DataTableHeader";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@tps/App/Components/Notify";
import DataTable, { DataTablePresentationProps } from "@tps/MobiusOverrides/DataTable/DataTable";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// XNG-Change: add fields to the widget
const enum fields {
    numberOfRecords = "numberOfRecords",
    customCSS = "customCSS",
    // XNG-Changes
    widgetTitle = "widgetTitle",
    showOrderDate = "showOrderDate",
    showOrderNumber = "showOrderNumber",
    showPONumber = "showPONumber",
    showStatus = "showStatus",
    showOrderTotal = "showOrderTotal",
    viewAllLinkText = "viewAllLinkText",
    // XNG-Changes: TPS-1112
    showOrderType = "showOrderType",
    showOrderedBy = "showOrderedBy",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.customCSS]: string;
        // XNG-Changes
        [fields.widgetTitle]: string;
        [fields.showOrderDate]: boolean;
        [fields.showOrderNumber]: boolean;
        [fields.showPONumber]: boolean;
        [fields.showStatus]: boolean;
        [fields.showOrderTotal]: boolean;
        [fields.viewAllLinkText]: string;
        // XNG-Changes: TPS-1112
        [fields.showOrderType]: boolean;
        [fields.showOrderedBy]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        ordersDataView: getOrdersDataView(state, recentOrdersParameter),
        orderHistoryPageLink: getPageLinkByPageType(state, "OrderHistoryPage"),
        settingsCollection: getSettingsCollection(state),
        canViewOrders: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
    };
};

const mapDispatchToProps = {
    loadOrders,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentOrdersStyles {
    cardList?: CardListStyles;
    errorGridItem?: GridItemProps;
    errorText?: TypographyPresentationProps;
    noOrdersFoundGridItem?: GridItemProps;
    noOrdersFoundText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    orderSummaryCard?: OrderSummaryCardStyles;
    // XNG-Changes:
    container?: InjectableCss;
    headingWrapper?: InjectableCss;
    widgetTitle?: TypographyPresentationProps;
    viewAllLink?: LinkPresentationProps;
    quotesContainer?: InjectableCss;
    noResults?: InjectableCss;
    dataTable?: DataTablePresentationProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeader?: DataTableHeaderPresentationProps;
    dataTableBody?: DataTableBodyProps;
}

export const recentOrdersStyles: RecentOrdersStyles = {
    errorGridItem: { width: 12 },
    noOrdersFoundGridItem: { width: 12 },
};

const defaultCustomCss = `.card-list-heading{
}

.view-all-link{
}

.no-orders-found-grid-item{
}

.description-grid-item{
}

.no-orders-found-text{
}

.card_container__wrapper{
}

.order-summary-card-grid-item{
}

.order-summary-card-container{
}

.page-type-link-grid-item{
}

.order-number-grid-item{
}

.status-grid-item{
}

.web-order-number-grid-item{
}

.total-grid-item{
}`;

let recentOrdersParameter: GetOrdersApiParameter = {
    customerSequence: "-1",
    page: 1,
    sort: "OrderDate DESC",
};

const styles = recentOrdersStyles;

class RecentOrders extends React.Component<Props> {
    componentDidMount() {
        recentOrdersParameter.pageSize = this.props.fields.numberOfRecords;
        // XNG-Change
        if (this.props.canViewOrders) {
            this.props.loadOrders(recentOrdersParameter);
        }
    }

    render() {
        const { ordersDataView, orderHistoryPageLink, settingsCollection, canViewOrders, fields } = this.props;
        if (!canViewOrders || !ordersDataView.value) {
            return null;
        }

        const orderHistoryUrl = orderHistoryPageLink ? orderHistoryPageLink.url : undefined;

        const customCssWrapper = {
            css: css`
                ${this.props.fields.customCSS}
            `,
        };

        return (
            // XNG-Changes: Change Structure to DataTable
            <StyledWrapper {...customCssWrapper} data-test-selector="recentOrders">
                <StyledWrapper {...styles.container}>
                    <StyledWrapper {...styles.headingWrapper}>
                        <Typography {...styles.widgetTitle}>{fields.widgetTitle}</Typography>
                        <Link {...styles.viewAllLink} href={orderHistoryUrl}>
                            {fields.viewAllLinkText}
                        </Link>
                    </StyledWrapper>
                    <StyledWrapper {...styles.quotesContainer}>
                        {ordersDataView.value.length === 0 && (
                            <Notify type="info" {...styles.noResults}>
                                {translate("No orders found")}
                            </Notify>
                        )}
                        {ordersDataView.value.length !== 0 && (
                            <DataTable {...styles.dataTable}>
                                <DataTableHead {...styles.dataTableHead}>
                                    {fields.showOrderDate && (
                                        <DataTableHeader tight title={translate("Date")} {...styles.dataTableHeader}>
                                            {translate("Date")}
                                        </DataTableHeader>
                                    )}
                                    {fields.showOrderNumber && (
                                        <DataTableHeader tight title={translate("Order #")} {...styles.dataTableHeader}>
                                            {translate("Order #")}
                                        </DataTableHeader>
                                    )}
                                    {/* XNG-Changes: TPS-1112 */}
                                    {fields.showOrderType && (
                                        <DataTableHeader
                                            tight
                                            title={translate("Order Type")}
                                            {...styles.dataTableHeader}
                                        >
                                            {translate("Order Type")}
                                        </DataTableHeader>
                                    )}
                                    {fields.showPONumber && (
                                        <DataTableHeader tight title={translate("PO #")} {...styles.dataTableHeader}>
                                            {translate("PO #")}
                                        </DataTableHeader>
                                    )}
                                    {/* XNG-Changes: TPS-1112 */}
                                    {fields.showOrderedBy && (
                                        <DataTableHeader
                                            tight
                                            title={translate("Ordered By")}
                                            {...styles.dataTableHeader}
                                        >
                                            {translate("Ordered By")}
                                        </DataTableHeader>
                                    )}
                                    {fields.showStatus && (
                                        <DataTableHeader tight title={translate("Status")} {...styles.dataTableHeader}>
                                            {translate("Status")}
                                        </DataTableHeader>
                                    )}
                                    {fields.showOrderTotal && (
                                        <DataTableHeader tight title={translate("Total")} {...styles.dataTableHeader}>
                                            {translate("Total")}
                                        </DataTableHeader>
                                    )}
                                </DataTableHead>
                                <DataTableBody {...styles.dataTableBody}>
                                    {ordersDataView.value.map(order => {
                                        return (
                                            <OrderSummaryCard
                                                key={order.id}
                                                order={order}
                                                orderSettings={settingsCollection.orderSettings}
                                                extendedStyles={styles.orderSummaryCard}
                                                {...{
                                                    showOrderDate: fields.showOrderDate,
                                                    showOrderNumber: fields.showOrderNumber,
                                                    showPONumber: fields.showPONumber,
                                                    showStatus: fields.showStatus,
                                                    showOrderTotal: fields.showOrderTotal,
                                                    // XNG-Changes: TPS-1112
                                                    showOrderType: fields.showOrderType,
                                                    showOrderedBy: fields.showOrderedBy,
                                                }}
                                            />
                                        );
                                    })}
                                </DataTableBody>
                            </DataTable>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentOrders),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Orders Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 3,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            // XNG-Changes
            {
                name: fields.widgetTitle,
                fieldType: "Contextual",
                displayName: translate("Title"),
                editorTemplate: "TextField",
                defaultValue: "Active Orders",
                tab: basicTab,
                sortOrder: 1,
            },
            {
                name: fields.viewAllLinkText,
                fieldType: "Contextual",
                displayName: translate("View All link text"),
                editorTemplate: "TextField",
                defaultValue: "View All Active Orders",
                tab: basicTab,
                sortOrder: 2,
            },
            {
                name: fields.showOrderDate,
                fieldType: "General",
                displayName: translate("Show Order Date"),
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tab: basicTab,
                sortOrder: 6,
            },
            {
                name: fields.showOrderNumber,
                fieldType: "General",
                displayName: translate("Show Order Number"),
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tab: basicTab,
                sortOrder: 7,
            },
            {
                name: fields.showPONumber,
                fieldType: "General",
                displayName: translate("Show PO #"),
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tab: basicTab,
                sortOrder: 8,
            },
            {
                name: fields.showStatus,
                fieldType: "General",
                displayName: translate("Show Order Status"),
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tab: basicTab,
                sortOrder: 9,
            },
            {
                name: fields.showOrderTotal,
                fieldType: "General",
                displayName: translate("Show Order Total"),
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tab: basicTab,
                sortOrder: 10,
            },
            // XNG-Changes: TPS-1112
            {
                name: fields.showOrderType,
                fieldType: "General",
                displayName: "Order Type",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tab: basicTab,
                sortOrder: 11,
            },
            {
                name: fields.showOrderedBy,
                fieldType: "General",
                displayName: "Ordered By",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tab: basicTab,
                sortOrder: 12,
            },
        ],
    },
};

export default widgetModule;
